import React from "react";
import {useCurrentFrame, useVideoConfig, interpolate, spring} from "remotion";
import {FONT_FAMILIES, loadGoogleFont} from "../../presets/fonts";

export interface LowerThirdProps {
  name: string;
  title?: string;
  accentColor?: string;
  backgroundColor?: string;
  textColor?: string;
  fontFamily?: string;
  position?: "bottomLeft" | "bottomRight" | "bottomCenter";
  enterDuration?: number;
  holdDuration?: number;
  exitDuration?: number;
  style?: React.CSSProperties;
}

export const LowerThird: React.FC<LowerThirdProps> = ({
  name,
  title,
  accentColor = "#6366f1",
  backgroundColor = "rgba(0, 0, 0, 0.85)",
  textColor = "#ffffff",
  fontFamily = FONT_FAMILIES.heading,
  position = "bottomLeft",
  enterDuration = 20,
  holdDuration = 90,
  exitDuration = 15,
  style,
}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();

  const fontName = fontFamily.replace(/'/g, "").split(",")[0].trim();
  loadGoogleFont(fontName);

  const totalDuration = enterDuration + holdDuration + exitDuration;
  const exitStart = enterDuration + holdDuration;

  if (frame > totalDuration) return null;

  // Enter animation
  const enterProgress = spring({
    fps,
    frame: Math.min(frame, enterDuration),
    config: {damping: 14, stiffness: 120},
  });

  // Exit animation
  const exitProgress =
    frame >= exitStart
      ? interpolate(frame, [exitStart, exitStart + exitDuration], [0, 1], {
          extrapolateLeft: "clamp",
          extrapolateRight: "clamp",
        })
      : 0;

  const translateX = frame < exitStart
    ? interpolate(enterProgress, [0, 1], [-100, 0])
    : interpolate(exitProgress, [0, 1], [0, -100]);

  const opacity = frame < exitStart
    ? interpolate(enterProgress, [0, 1], [0, 1])
    : 1 - exitProgress;

  const positionStyles: React.CSSProperties = {
    bottomLeft: {bottom: 80, left: 60},
    bottomRight: {bottom: 80, right: 60},
    bottomCenter: {bottom: 80, left: "50%", transform: "translateX(-50%)"},
  }[position];

  return (
    <div
      style={{
        position: "absolute",
        ...positionStyles,
        opacity,
        transform: position === "bottomCenter"
          ? `translateX(calc(-50% + ${translateX}%))`
          : `translateX(${translateX}%)`,
        ...style,
      }}
    >
      {/* Accent bar */}
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          backgroundColor: accentColor,
          borderRadius: 2,
        }}
      />
      <div
        style={{
          backgroundColor,
          padding: "16px 28px 16px 20px",
          marginLeft: 4,
          borderRadius: "0 6px 6px 0",
          backdropFilter: "blur(10px)",
        }}
      >
        <div
          style={{
            fontSize: 28,
            fontFamily,
            fontWeight: 700,
            color: textColor,
            lineHeight: 1.2,
          }}
        >
          {name}
        </div>
        {title && (
          <div
            style={{
              fontSize: 18,
              fontFamily,
              fontWeight: 400,
              color: accentColor,
              marginTop: 4,
              lineHeight: 1.2,
            }}
          >
            {title}
          </div>
        )}
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
